import type { Nullish, ProductWeight } from '@iheartjane/types';
import type { Offer } from 'schema-dts';

import { PRICE_ID_TO_GRAMS } from '../priceIdToGrams';

/** @see https://developers.google.com/search/docs/appearance/structured-data/product-snippet#offer-details */

interface OffersArgs {
  currency_code: string;
  price_each?: Nullish<string | number>;
  price_eighth_ounce?: Nullish<string | number>;
  price_gram?: Nullish<string | number>;
  price_half_gram?: Nullish<string | number>;
  price_half_ounce?: Nullish<string | number>;
  price_ounce?: Nullish<string | number>;
  price_quarter_ounce?: Nullish<string | number>;
  price_two_gram?: Nullish<string | number>;
}

const PRICING_KEYS: (keyof Omit<OffersArgs, 'currency_code'>)[] = [
  'price_each',
  'price_eighth_ounce',
  'price_gram',
  'price_half_gram',
  'price_half_ounce',
  'price_ounce',
  'price_quarter_ounce',
  'price_two_gram',
];

/**
 * @see https://developers.google.com/search/docs/appearance/structured-data/merchant-listing
 * @see https://schema.org/Offer
 *
 * Note: this is a minimal implementation of an Offer schema, but can be expanded to represent more pricing details:
 * @see https://developers.google.com/search/docs/appearance/structured-data/merchant-listing#sale-pricing-example for discounted pricing
 * @see https://developers.google.com/search/docs/appearance/structured-data/merchant-listing#member-price-example for member pricing (ie. Jane Gold)
 */
export const offers = ({
  currency_code,
  ...pricing
}: OffersArgs): { offers: Offer } => {
  const priceSpecification = PRICING_KEYS.flatMap((key) => {
    const weightedPrice = pricing[key];
    if (!weightedPrice) return [];
    const priceId = key.replace('price_', '') as ProductWeight;
    const grams = PRICE_ID_TO_GRAMS[priceId];
    return [
      {
        '@type': 'UnitPriceSpecification' as const,
        price:
          typeof weightedPrice === 'number'
            ? weightedPrice
            : parseFloat(weightedPrice),
        priceCurrency: currency_code,
        ...(grams
          ? {
              referenceQuantity: {
                '@type': 'QuantitativeValue' as const,
                value: `${grams}`,
                unitCode: 'g',
              },
            }
          : {}),
      },
    ];
  });

  if (priceSpecification.length === 0) {
    /**
     * When unavailable, an offer of price 0 must be provided
     * and availability must be set to out of stock or something similar.
     */
    return {
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/OutOfStock',
        price: '0',
        priceCurrency: currency_code,
      },
    };
  }

  return {
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceSpecification,
    },
  };
};
